import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, User, Camera, Check, ArrowRight, KeyRound, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_AVATARS = [
  "felix", "aneka", "nala", "oliver", "milo", "sophie",
  "luna", "jasper", "zara", "leo", "coco", "max",
].map(seed => `https://api.dicebear.com/7.x/fun-emoji/svg?seed=${seed}`);

const Auth = () => {
  const { register, login, loginWithPin, user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialMode = searchParams.get("mode") === "signup" ? "register" : "login";
  const [mode, setMode] = useState(initialMode); // login | register | pin | forgot | reset

  // If already signed in, bounce to home (Auth is now a public route)
  useEffect(() => {
    if (user) navigate("/home", { replace: true });
  }, [user, navigate]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(PRESET_AVATARS[0]);
  const [customAvatar, setCustomAvatar] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPin, setNewPin] = useState("");
  const [phone, setPhone] = useState("");
  const fileRef = useRef(null);

  // Capture referral code from URL on Auth page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("referral_code", ref);
  }, []);

  const handleCustomAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${BACKEND_URL}/api/community/upload`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        const url = data.url || data.file_url;
        setCustomAvatar(url);
        setSelectedAvatar(url);
      }
    } catch (e) { /* ignore */ }
    setUploading(false);
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      if (mode === "register") {
        if (!name.trim()) { setError("Name is required"); setLoading(false); return; }
        if (!email.trim()) { setError("Email is required"); setLoading(false); return; }
        if (password.length < 6) { setError("Password must be at least 6 characters"); setLoading(false); return; }
        if (pin.length < 4 || !/^\d+$/.test(pin)) { setError("PIN must be at least 4 digits"); setLoading(false); return; }
        await register(name.trim(), email.trim(), password, pin, selectedAvatar, phone.trim());
        // Flag so the /home page shows a welcome toast on first mount
        try { sessionStorage.setItem("just_signed_up", "1"); } catch { /* ignore */ }
      } else if (mode === "pin") {
        await loginWithPin(email.trim(), pin);
      } else if (mode === "forgot") {
        if (!email.trim()) { setError("Enter your email to receive a reset code"); setLoading(false); return; }
        const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim() }),
        });
        if (res.ok) {
          setSuccess("Reset code sent! Check your email.");
          setMode("reset");
        } else {
          const d = await res.json();
          setError(d.detail || "Failed to send reset code");
        }
      } else if (mode === "reset") {
        if (!resetCode || resetCode.length < 6) { setError("Enter the 6-digit code from your email"); setLoading(false); return; }
        if (!newPassword && !newPin) { setError("Enter a new password or PIN"); setLoading(false); return; }
        if (newPassword && newPassword.length < 6) { setError("Password must be at least 6 characters"); setLoading(false); return; }
        if (newPin && (newPin.length < 4 || !/^\d+$/.test(newPin))) { setError("PIN must be at least 4 digits"); setLoading(false); return; }
        const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim(), code: resetCode, new_password: newPassword || undefined, new_pin: newPin || undefined }),
        });
        if (res.ok) {
          setSuccess("Credentials updated! You can now sign in.");
          setTimeout(() => { setMode("login"); setSuccess(""); setResetCode(""); setNewPassword(""); setNewPin(""); }, 2000);
        } else {
          const d = await res.json();
          setError(d.detail || "Reset failed");
        }
      } else {
        await login(email.trim(), password);
      }
    } catch (e) {
      setError(e.message || "Something went wrong");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-gray-50 flex flex-col items-center justify-center p-4 relative overflow-hidden" data-testid="auth-page">
      {/* Animated background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-green-200/30 rounded-full blur-3xl animate-[float_8s_ease-in-out_infinite]" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-emerald-200/25 rounded-full blur-3xl animate-[float_10s_ease-in-out_infinite_reverse]" />
        <div className="absolute top-1/3 right-10 w-40 h-40 bg-green-300/20 rounded-full blur-2xl animate-[float_6s_ease-in-out_infinite_1s]" />
      </div>

      <div className="w-full max-w-sm space-y-6 relative z-10">
        {/* Logo & Tagline */}
        <div className="text-center space-y-2">
          <img
            src="/logo-auth.png"
            alt="Foodz Lizts"
            className="w-36 h-36 mx-auto mb-1 object-contain drop-shadow-lg"
            data-testid="auth-logo"
          />
          <h1 className="text-3xl font-bold text-green-600">Foodz Lizts</h1>
          <p className="text-sm font-medium bg-gradient-to-r from-blue-500 via-gray-900 to-green-500 bg-[length:200%_100%] animate-[shimmer_3s_ease-in-out_infinite] bg-clip-text text-transparent tracking-wide">
            Your All in One Grocery Budget Shopping Assistant
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {mode === "register" ? "Create your account" : mode === "pin" ? "Quick PIN login" : mode === "forgot" ? "Reset your credentials" : mode === "reset" ? "Enter your reset code" : "Welcome back"}
          </p>
        </div>

        <Card>
          <CardContent className="p-5 space-y-4">
            {/* Avatar Selection (register only) */}
            {mode === "register" && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Choose your avatar</p>
                <div className="flex items-center gap-3 mb-2">
                  <img
                    src={selectedAvatar}
                    alt="avatar"
                    className="w-16 h-16 rounded-full border-2 border-green-400 object-cover bg-gray-100"
                    data-testid="selected-avatar"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 font-medium">Your avatar</p>
                    <button
                      className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 mt-0.5"
                      onClick={() => fileRef.current?.click()}
                      data-testid="upload-avatar-btn"
                    >
                      <Camera className="h-3 w-3" />
                      {uploading ? "Uploading..." : "Upload custom photo"}
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleCustomAvatar} />
                  </div>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {PRESET_AVATARS.map((url, i) => (
                    <button
                      key={i}
                      className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-all ${
                        selectedAvatar === url ? "border-green-500 ring-2 ring-green-200" : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => { setSelectedAvatar(url); setCustomAvatar(null); }}
                      data-testid={`avatar-option-${i}`}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover bg-gray-50" />
                    </button>
                  ))}
                </div>
                {customAvatar && (
                  <button
                    className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-all ${
                      selectedAvatar === customAvatar ? "border-green-500 ring-2 ring-green-200" : "border-gray-200"
                    }`}
                    onClick={() => setSelectedAvatar(customAvatar)}
                  >
                    <img src={customAvatar} alt="" className="w-full h-full object-cover" />
                  </button>
                )}
              </div>
            )}

            {/* Name (register only) */}
            {mode === "register" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Your name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="pl-9"
                  data-testid="name-input"
                />
              </div>
            )}

            {/* Phone Number (register only — optional) */}
            {mode === "register" && (
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="tel"
                  placeholder="Phone number (optional)"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="pl-9"
                  data-testid="phone-input"
                />
              </div>
            )}

            {/* Email */}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="pl-9"
                data-testid="email-input"
              />
            </div>

            {/* Password (login + register only) */}
            {(mode === "login" || mode === "register") && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type={showPw ? "text" : "password"}
                  placeholder={mode === "register" ? "Create password (6+ chars)" : "Password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="password-input"
                />
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPw(!showPw)}
                  type="button"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            )}

            {/* PIN (register + pin-login) */}
            {(mode === "register" || mode === "pin") && (
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={mode === "register" ? "Create PIN (4+ digits)" : "Enter your PIN"}
                  value={pin}
                  onChange={e => setPin(e.target.value.replace(/\D/g, ""))}
                  className="pl-9"
                  data-testid="pin-input"
                />
              </div>
            )}

            {/* Forgot Mode — just email field (already shown above) */}

            {/* Reset Mode — code + new credentials */}
            {mode === "reset" && (
              <>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit reset code"
                    value={resetCode}
                    onChange={e => setResetCode(e.target.value.replace(/\D/g, ""))}
                    className="pl-9 text-center text-lg tracking-widest font-mono"
                    data-testid="reset-code-input"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type={showPw ? "text" : "password"}
                    placeholder="New password (6+ chars)"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="pl-9 pr-9"
                    data-testid="new-password-input"
                  />
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPw(!showPw)}
                    type="button"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="New PIN (4+ digits, optional)"
                    value={newPin}
                    onChange={e => setNewPin(e.target.value.replace(/\D/g, ""))}
                    className="pl-9"
                    data-testid="new-pin-input"
                  />
                </div>
              </>
            )}

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500 text-center" data-testid="auth-error">{error}</p>
            )}

            {/* Success */}
            {success && (
              <p className="text-sm text-green-600 text-center" data-testid="auth-success">{success}</p>
            )}

            {/* Submit */}
            <Button
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold"
              onClick={handleSubmit}
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? "..." :
                mode === "register" ? "Create Account" :
                mode === "pin" ? "Login with PIN" :
                mode === "forgot" ? "Send Reset Code" :
                mode === "reset" ? "Reset Credentials" :
                "Sign In"}
              {!loading && <ArrowRight className="h-4 w-4 ml-2" />}
            </Button>

            {/* Mode toggles */}
            <div className="text-center space-y-1.5">
              {mode === "login" && (
                <>
                  <button className="text-sm text-green-600 hover:text-green-700" onClick={() => { setMode("pin"); setError(""); setSuccess(""); }} data-testid="switch-to-pin">
                    <KeyRound className="h-3 w-3 inline mr-1" /> Use PIN instead
                  </button>
                  <p className="text-xs text-gray-400">
                    <button className="text-green-600 font-medium hover:text-green-700" onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }} data-testid="forgot-password-link">
                      Forgot password?
                    </button>
                  </p>
                  <p className="text-xs text-gray-400">
                    Don't have an account?{" "}
                    <button className="text-green-600 font-medium hover:text-green-700" onClick={() => { setMode("register"); setError(""); setSuccess(""); }} data-testid="switch-to-register">
                      Sign up
                    </button>
                  </p>
                </>
              )}
              {mode === "pin" && (
                <>
                  <button className="text-sm text-green-600 hover:text-green-700" onClick={() => { setMode("login"); setError(""); setSuccess(""); }} data-testid="switch-to-password">
                    <Lock className="h-3 w-3 inline mr-1" /> Use password instead
                  </button>
                  <p className="text-xs text-gray-400">
                    <button className="text-green-600 font-medium hover:text-green-700" onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }} data-testid="forgot-pin-link">
                      Forgot PIN?
                    </button>
                  </p>
                  <p className="text-xs text-gray-400">
                    Don't have an account?{" "}
                    <button className="text-green-600 font-medium hover:text-green-700" onClick={() => { setMode("register"); setError(""); setSuccess(""); }}>
                      Sign up
                    </button>
                  </p>
                </>
              )}
              {mode === "register" && (
                <p className="text-xs text-gray-400">
                  Already have an account?{" "}
                  <button className="text-green-600 font-medium hover:text-green-700" onClick={() => { setMode("login"); setError(""); setSuccess(""); }} data-testid="switch-to-login">
                    Sign in
                  </button>
                </p>
              )}
              {(mode === "forgot" || mode === "reset") && (
                <p className="text-xs text-gray-400">
                  Remember your credentials?{" "}
                  <button className="text-green-600 font-medium hover:text-green-700" onClick={() => { setMode("login"); setError(""); setSuccess(""); setResetCode(""); setNewPassword(""); setNewPin(""); }} data-testid="back-to-login">
                    Back to login
                  </button>
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Legal Links */}
        <div className="text-center mt-3">
          <p className="text-[10px] text-gray-400">
            By using Foodz Lizts, you agree to our{" "}
            <a href="/terms" className="underline hover:text-gray-500">Terms of Service</a>
            {" "}and{" "}
            <a href="/privacy" className="underline hover:text-gray-500">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
